import { Carousel, Icon, Lightbox, LightboxController } from '@design-system';
import classNames from 'classnames';
import { useTranslation } from 'next-i18next';
import React, { useMemo } from 'react';

import { VimeoPlugin } from '../../../types/Vimeo';
import {
  TrackCarouselSlide,
  TrackLinkClicked,
  TrackMultiMediaClick,
} from '../../../utils/analytics';
import { getArtistTileOverline } from '../../../utils/artists';
import { storyblokMediaItemToLightboxEntry } from '../../../utils/lightbox';
import VideoCard from '../../VideoCard';
import { ArtistTileInline } from '../ArtistTileInline';

type ArtistVideo = {
  coverImage: StoryblokImage;
  vimeo: VimeoPlugin;
  artist: ArtistPageContent;
  artistMetadata: {
    totalWorks: number;
    hideCollaborations: boolean;
  };
};

type Props = {
  headline: string;
  perPage: number;
  videos: ArtistVideo[];
  analytics: Analytics;
  withSeeAll?: boolean;
};

const ArtistVideosCarousel: React.FC<Props> = (props) => {
  const lightboxItems = useMemo(
    () => (props.videos as any[]).map(storyblokMediaItemToLightboxEntry),
    [props.videos],
  );

  return (
    <Lightbox items={lightboxItems}>
      {(lightbox) => <VideoCarousel lightbox={lightbox} {...props} />}
    </Lightbox>
  );
};

export default ArtistVideosCarousel;

const VideoCarousel: React.FC<
  Props & {
    lightbox: LightboxController;
  }
> = ({ analytics, headline, lightbox, perPage, videos, withSeeAll }) => {
  const { t } = useTranslation('artists');

  const carouselItems = useMemo(() => {
    const items = [];

    videos.forEach((video, index) => {
      items.push(
        <VideoCarouselItem
          analytics={analytics}
          onClick={() => {
            TrackMultiMediaClick({
              page: analytics.page,
              context: analytics.context,
              pageTitle: analytics.pageTitle,
              type: 'video',
              title: video.vimeo?.vimeo_oembed.response.title,
              durationInSeconds: video.vimeo?.vimeo_oembed.response.duration,
              position: index,
            });
            lightbox.openOnSlide(index);
          }}
          video={video}
        />,
      );
    });

    if (withSeeAll) {
      items.push(
        <SeeAllCarouselItem
          onClick={() => {
            TrackLinkClicked({
              page: analytics.page,
              context: analytics.context,
              title: t('viewAllStudioVisits'),
              url: 'https://youtube.com/c/AvantArte',
              isExternal: true,
            });
          }}
        />,
      );
    }

    return items;
  }, [videos, analytics, withSeeAll]);

  return (
    <Carousel
      headline={headline}
      items={carouselItems}
      onSlide={(idx) => {
        analytics &&
          TrackCarouselSlide({
            page: analytics.page,
            context: analytics.context,
            title: analytics.context,
            position: idx,
            carouselLength: videos.length,
          });
      }}
      perPage={perPage}
    />
  );
};

const VideoCarouselItem: React.FC<{
  analytics: Analytics;
  video: ArtistVideo;
  onClick: () => void;
}> = ({ analytics, onClick, video }) => {
  const { t } = useTranslation('artists');

  return (
    <section>
      <VideoCard
        coverImage={video.coverImage}
        onClick={onClick}
        video={video.vimeo}
      />

      <div className="pt-4">
        <ArtistTileInline
          artist={{
            name: video.artist.name,
            slug: video.artist.full_slug,
            thumbnail: video.artist.content.thumbnail,
          }}
          overline={getArtistTileOverline(
            video.artistMetadata.totalWorks,
            video.artistMetadata.hideCollaborations,
            t,
          )}
          page={analytics.page}
        />
      </div>
    </section>
  );
};

const SeeAllCarouselItem: React.FC<{ onClick: () => void }> = ({ onClick }) => {
  const { t } = useTranslation('artists');

  return (
    <a
      className={classNames(
        'flex aspect-[1/1] w-full items-center justify-center rounded-sm bg-neutral-1 transition duration-300 ease-out hover:text-neutral-4 md:aspect-[3/2]',
      )}
      href="https://youtube.com/c/AvantArte"
      onClick={onClick}
      rel="noreferrer"
      target="_blank"
    >
      <div className="flex w-full flex-col items-center justify-center space-y-3">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-neutral-2">
          <Icon name="ic_arrow_top_right" size="md" />
        </div>
        <span className="text-center">{t('viewAllStudioVisits')}</span>
      </div>
    </a>
  );
};
